import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

interface OfertaTrabajo {
  id: number;
  titulo: string;
  empresa: string;
  ubicacion: string;
  salario: string;
  tipo: string;
  descripcion: string;
  requisitos: string[];
  beneficios: string[];
  fechaPublicacion: string;
  estado: 'activa' | 'cerrada' | 'próximamente';
}

@Component({
  selector: 'app-oportunidades',
  templateUrl: './oportunidades.component.html',
  styleUrls: ['./oportunidades.component.css']
})
export class OportunidadesComponent implements OnInit {
  
  ofertasTrabajo: OfertaTrabajo[] = [];
  filtros = {
    ubicacion: '',
    tipo: '',
    empresa: '',
    estado: 'todos'
  };
  
  ubicacionesDisponibles: string[] = [];
  tiposDisponibles: string[] = [];
  empresasDisponibles: string[] = [];

  constructor(private router: Router) { }

  ngOnInit(): void {
    this.cargarOfertas();
    this.extraerFiltrosDisponibles();
  }

  cargarOfertas(): void {
    // Datos de ejemplo - en producción vendrían de un servicio
    this.ofertasTrabajo = [
      {
        id: 1,
        titulo: 'Desarrollador Full Stack Senior',
        empresa: 'TechCorp Solutions',
        ubicacion: 'Bogotá, Colombia',
        salario: '$8.000.000 - $12.000.000 COP',
        tipo: 'Tiempo completo',
        descripcion: 'Buscamos un desarrollador Full Stack Senior con experiencia en tecnologías modernas para liderar proyectos de desarrollo.',
        requisitos: [
          '5+ años de experiencia en desarrollo web',
          'Conocimiento sólido en Angular, Node.js y bases de datos',
          'Experiencia con metodologías ágiles',
          'Inglés intermedio-avanzado'
        ],
        beneficios: [
          'Horario flexible',
          'Trabajo remoto híbrido',
          'Capacitación continua',
          'Seguro médico privado'
        ],
        fechaPublicacion: '2024-01-15',
        estado: 'activa'
      },
      {
        id: 2,
        titulo: 'UX/UI Designer',
        empresa: 'Digital Innovations',
        ubicacion: 'Medellín, Colombia',
        salario: '$6.000.000 - $9.000.000 COP',
        tipo: 'Tiempo completo',
        descripcion: 'Diseñador UX/UI creativo para crear experiencias digitales excepcionales y centradas en el usuario.',
        requisitos: [
          '3+ años de experiencia en diseño UX/UI',
          'Manejo de Figma, Adobe Creative Suite',
          'Portfolio demostrable',
          'Conocimiento de metodologías de investigación'
        ],
        beneficios: [
          'Ambiente creativo',
          'Proyectos innovadores',
          'Desarrollo profesional',
          'Beneficios de salud'
        ],
        fechaPublicacion: '2024-01-10',
        estado: 'activa'
      },
      {
        id: 3,
        titulo: 'Project Manager',
        empresa: 'Global Systems',
        ubicacion: 'Cali, Colombia',
        salario: '$7.500.000 - $11.000.000 COP',
        tipo: 'Tiempo completo',
        descripcion: 'Project Manager con experiencia en gestión de proyectos tecnológicos y liderazgo de equipos.',
        requisitos: [
          '4+ años en gestión de proyectos',
          'Certificación PMP o similar',
          'Experiencia con metodologías ágiles',
          'Liderazgo de equipos de 5+ personas'
        ],
        beneficios: [
          'Liderazgo de proyectos estratégicos',
          'Capacitación en metodologías',
          'Bonos por resultados',
          'Seguro de vida'
        ],
        fechaPublicacion: '2024-01-08',
        estado: 'activa'
      },
      {
        id: 4,
        titulo: 'Data Scientist',
        empresa: 'Analytics Pro',
        ubicacion: 'Barranquilla, Colombia',
        salario: '$9.000.000 - $14.000.000 COP',
        tipo: 'Tiempo completo',
        descripcion: 'Científico de datos para desarrollar soluciones analíticas y modelos predictivos innovadores.',
        requisitos: [
          'Maestría en estadística, matemáticas o afines',
          'Experiencia con Python, R, SQL',
          'Conocimiento de machine learning',
          'Publicaciones o proyectos demostrables'
        ],
        beneficios: [
          'Investigación aplicada',
          'Colaboración con universidades',
          'Conferencias internacionales',
          'Horario flexible'
        ],
        fechaPublicacion: '2024-01-05',
        estado: 'activa'
      },
      {
        id: 5,
        titulo: 'DevOps Engineer',
        empresa: 'Cloud Solutions',
        ubicacion: 'Bucaramanga, Colombia',
        salario: '$8.500.000 - $13.000.000 COP',
        tipo: 'Tiempo completo',
        descripcion: 'Ingeniero DevOps para automatizar procesos de desarrollo y despliegue en entornos cloud.',
        requisitos: [
          '3+ años en DevOps o infraestructura',
          'AWS, Azure o GCP',
          'Docker, Kubernetes',
          'CI/CD pipelines'
        ],
        beneficios: [
          'Certificaciones cloud',
          'Trabajo remoto',
          'Tecnologías de vanguardia',
          'Bonos por rendimiento'
        ],
        fechaPublicacion: '2024-01-03',
        estado: 'activa'
      },
      {
        id: 6,
        titulo: 'Frontend Developer',
        empresa: 'Web Masters',
        ubicacion: 'Pereira, Colombia',
        salario: '$5.500.000 - $8.500.000 COP',
        tipo: 'Tiempo completo',
        descripcion: 'Desarrollador Frontend especializado en crear interfaces web modernas y responsivas.',
        requisitos: [
          '2+ años en desarrollo frontend',
          'HTML5, CSS3, JavaScript',
          'React, Vue o Angular',
          'Diseño responsivo'
        ],
        beneficios: [
          'Proyectos variados',
          'Mentoría senior',
          'Capacitación técnica',
          'Ambiente colaborativo'
        ],
        fechaPublicacion: '2024-01-01',
        estado: 'activa'
      }
    ];
  }

  extraerFiltrosDisponibles(): void {
    this.ubicacionesDisponibles = Array.from(new Set(this.ofertasTrabajo.map(o => o.ubicacion)));
    this.tiposDisponibles = Array.from(new Set(this.ofertasTrabajo.map(o => o.tipo)));
    this.empresasDisponibles = Array.from(new Set(this.ofertasTrabajo.map(o => o.empresa)));
  }

  aplicarFiltros(): OfertaTrabajo[] {
    return this.ofertasTrabajo.filter(oferta => {
      const cumpleUbicacion = !this.filtros.ubicacion || oferta.ubicacion.includes(this.filtros.ubicacion);
      const cumpleTipo = !this.filtros.tipo || oferta.tipo === this.filtros.tipo;
      const cumpleEmpresa = !this.filtros.empresa || oferta.empresa.includes(this.filtros.empresa);
      const cumpleEstado = this.filtros.estado === 'todos' || oferta.estado === this.filtros.estado;
      
      return cumpleUbicacion && cumpleTipo && cumpleEmpresa && cumpleEstado;
    });
  }

  limpiarFiltros(): void {
    this.filtros = {
      ubicacion: '',
      tipo: '',
      empresa: '',
      estado: 'todos'
    };
  }

  aplicarOferta(oferta: OfertaTrabajo): void {
    console.log('Redirigiendo a contacto para aplicar a oferta:', oferta.titulo);
    this.router.navigate(['/contacto']);
  }

  guardarOferta(oferta: OfertaTrabajo): void {
    console.log('Guardando oferta:', oferta.titulo);
    // Aquí se implementaría la lógica para guardar la oferta
    alert(`Oferta guardada: ${oferta.titulo}`);
  }

  compartirOferta(oferta: OfertaTrabajo): void {
    console.log('Compartiendo oferta:', oferta.titulo);
    // Aquí se implementaría la lógica para compartir la oferta
    if ('share' in navigator) {
      (navigator as any).share({
        title: oferta.titulo,
        text: `Mira esta oportunidad en ${oferta.empresa}: ${oferta.titulo}`,
        url: window.location.href
      }).catch(() => {
        this.copiarAlPortapapeles();
      });
    } else {
      // Fallback para navegadores que no soportan Web Share API
      this.copiarAlPortapapeles();
    }
  }

  private copiarAlPortapapeles(): void {
    const url = window.location.href;
    if ('clipboard' in navigator) {
      (navigator as any).clipboard.writeText(url).then(() => {
        alert('Enlace copiado al portapapeles');
      }).catch(() => {
        this.fallbackCopiarAlPortapapeles(url);
      });
    } else {
      this.fallbackCopiarAlPortapapeles(url);
    }
  }

  private fallbackCopiarAlPortapapeles(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      alert('Enlace copiado al portapapeles');
    } catch (err) {
      alert('No se pudo copiar el enlace');
    }
    document.body.removeChild(textArea);
  }

  obtenerOfertasFiltradas(): OfertaTrabajo[] {
    return this.aplicarFiltros();
  }

  getEstadoClass(estado: string): string {
    switch (estado) {
      case 'activa': return 'badge-success';
      case 'cerrada': return 'badge-danger';
      case 'próximamente': return 'badge-warning';
      default: return 'badge-secondary';
    }
  }

  getEstadoText(estado: string): string {
    switch (estado) {
      case 'activa': return 'Activa';
      case 'cerrada': return 'Cerrada';
      case 'próximamente': return 'Próximamente';
      default: return 'Desconocido';
    }
  }
}
